<template>
  <div class="vehicle-diagram">
    <v-card outlined>
      <v-card-text>
        <v-row>
          <v-col cols="12" md="8">
            <!-- Canvas para dibujar daños -->
            <v-card outlined class="canvas-container">
              <v-card-subtitle class="subtitle-2 font-weight-bold primary white--text py-2">
                <v-icon left small color="white">mdi-car-outline</v-icon>
                Diagrama del Vehículo - Marque los Daños
              </v-card-subtitle>

              <v-card-text>
                <div class="canvas-wrapper" ref="canvasWrapper">
                  <canvas
                    ref="canvas"
                    @mousedown="startDrawing"
                    @mousemove="draw"
                    @mouseup="stopDrawing"
                    @mouseleave="stopDrawing"
                    @touchstart="handleTouch"
                    @touchmove="handleTouch"
                    @touchend="stopDrawing"
                  ></canvas>
                </div>

                <v-divider class="my-3"></v-divider>

                <!-- Herramientas -->
                <v-row align="center">
                  <v-col cols="12" sm="auto">
                    <v-btn-toggle v-model="currentTool" mandatory>
                      <v-btn value="mark" small>
                        <v-icon>mdi-map-marker</v-icon>
                        Marcar
                      </v-btn>
                      <v-btn value="draw" small>
                        <v-icon>mdi-pencil</v-icon>
                        Dibujar
                      </v-btn>
                      <v-btn value="erase" small>
                        <v-icon>mdi-eraser</v-icon>
                        Borrar
                      </v-btn>
                    </v-btn-toggle>
                  </v-col>

                  <v-col cols="12" sm="auto">
                    <v-select
                      v-model="damageType"
                      :items="damageTypes"
                      label="Tipo de Daño"
                      dense
                      outlined
                      hide-details
                      style="max-width: 200px;"
                    ></v-select>
                  </v-col>

                  <v-col cols="12" sm="auto">
                    <v-menu
                      v-model="colorMenu"
                      :close-on-content-click="false"
                      offset-y
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          :color="currentColor"
                          v-bind="attrs"
                          v-on="on"
                          small
                        >
                          <v-icon left>mdi-palette</v-icon>
                          Color
                        </v-btn>
                      </template>
                      <v-card>
                        <v-card-text>
                          <v-color-picker
                            v-model="currentColor"
                            hide-mode-switch
                            mode="hexa"
                          ></v-color-picker>
                        </v-card-text>
                      </v-card>
                    </v-menu>
                  </v-col>

                  <v-col cols="12" sm="auto">
                    <v-slider
                      v-model="brushSize"
                      :min="1"
                      :max="20"
                      label="Tamaño"
                      hide-details
                      thumb-label
                      style="max-width: 150px;"
                    ></v-slider>
                  </v-col>

                  <v-spacer></v-spacer>

                  <v-col cols="12" sm="auto">
                    <v-btn
                      color="warning"
                      outlined
                      small
                      @click="clearCanvas"
                    >
                      <v-icon left>mdi-delete-sweep</v-icon>
                      Limpiar Vista
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Lista de Daños Marcados -->
          <v-col cols="12" md="4">
            <v-card outlined>
              <v-card-subtitle class="font-weight-bold">
                <v-icon left>mdi-format-list-bulleted</v-icon>
                Daños Registrados ({{ damages.length }})
              </v-card-subtitle>
              <v-card-text>
                <v-list dense>
                  <v-list-item
                    v-for="(damage, index) in damages"
                    :key="index"
                    :class="{ 'selected-damage': selectedDamageIndex === index }"
                  >
                    <v-list-item-avatar :color="damage.color" size="30">
                      <span class="white--text caption">{{ index + 1 }}</span>
                    </v-list-item-avatar>

                    <v-list-item-content>
                      <v-list-item-title class="text-caption">
                        {{ damage.tipo }}
                      </v-list-item-title>
                      <v-list-item-subtitle class="text-caption">
                        {{ damage.vista }} - {{ damage.ubicacion }}
                      </v-list-item-subtitle>
                      
                      <v-text-field
                        v-model="damage.descripcion"
                        placeholder="Descripción adicional..."
                        dense
                        hide-details
                        class="mt-1"
                        @input="emitUpdate"
                      ></v-text-field>
                    </v-list-item-content>

                    <v-list-item-action>
                      <v-btn
                        icon
                        x-small
                        @click="removeDamage(index)"
                      >
                        <v-icon small color="error">mdi-delete</v-icon>
                      </v-btn>
                    </v-list-item-action>
                  </v-list-item>

                  <v-list-item v-if="damages.length === 0">
                    <v-list-item-content>
                      <v-list-item-title class="text-center text-caption grey--text">
                        No hay daños registrados
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
export default {
  name: 'VehicleDiagram',
  
  props: {
    value: {
      type: Object,
      default: () => ({ marcas_daños: [] })
    }
  },
  
  data() {
    return {
      currentTool: 'mark',
      damageType: 'Rayón',
      currentColor: '#FF0000',
      brushSize: 5,
      colorMenu: false,
      
      damageTypes: [
        'Rayón',
        'Golpe',
        'Abolladura',
        'Ruptura',
        'Desgaste',
        'Corrosión',
        'Fisura',
        'Desprendimiento',
        'Otro'
      ],
      
      damages: [],
      selectedDamageIndex: null,
      
      isDrawing: false,
      lastX: 0,
      lastY: 0,
      
      vehicleImage: null,
      imageLoaded: false
    };
  },
  
  watch: {
    value: {
      handler(newValue) {
        if (newValue && newValue.marcas_daños) {
          this.damages = [...newValue.marcas_daños];
          this.redrawCanvas();
        }
      },
      deep: true
    }
  },
  
  mounted() {
    this.loadVehicleImage();
    
    if (this.value && this.value.marcas_daños) {
      this.damages = [...this.value.marcas_daños];
    }
    
    // Ajustar canvas en resize
    window.addEventListener('resize', this.resizeCanvas);
  },
  
  beforeDestroy() {
    window.removeEventListener('resize', this.resizeCanvas);
  },
  
  methods: {
    loadVehicleImage() {
      this.vehicleImage = new Image();
      this.vehicleImage.onload = () => {
        this.imageLoaded = true;
        this.initCanvas();
      };
      this.vehicleImage.onerror = () => {
        console.error('Error loading vehicle image');
        this.initCanvas();
      };
      this.vehicleImage.src = require('@/assets/car.jpg');
    },
    
    initCanvas() {
      const canvas = this.$refs.canvas;
      const wrapper = this.$refs.canvasWrapper;
      
      if (this.imageLoaded && this.vehicleImage) {
        // Ajustar el canvas al tamaño de la imagen manteniendo proporción
        const maxWidth = wrapper.offsetWidth || 800;
        const aspectRatio = this.vehicleImage.height / this.vehicleImage.width;
        canvas.width = maxWidth;
        canvas.height = maxWidth * aspectRatio;
      } else {
        canvas.width = wrapper.offsetWidth || 800;
        canvas.height = 600;
      }
      
      this.drawVehicleTemplate();
    },
    
    resizeCanvas() {
      const canvas = this.$refs.canvas;
      const wrapper = this.$refs.canvasWrapper;
      
      // Guardar el contenido actual
      const imageData = canvas.toDataURL();
      
      // Redimensionar
      canvas.width = wrapper.offsetWidth || 600;
      canvas.height = 400;
      
      // Restaurar contenido
      const img = new Image();
      img.onload = () => {
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
      };
      img.src = imageData;
    },
    
    drawVehicleTemplate() {
      const canvas = this.$refs.canvas;
      const ctx = canvas.getContext('2d');
      
      // Limpiar canvas
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Dibujar la imagen del vehículo si está cargada
      if (this.imageLoaded && this.vehicleImage) {
        ctx.drawImage(this.vehicleImage, 0, 0, canvas.width, canvas.height);
      }
      
      // Redibujar todos los daños
      this.redrawDamages();
    },
    
    redrawDamages() {
      const canvas = this.$refs.canvas;
      const ctx = canvas.getContext('2d');
      
      // Dibujar todos los daños
      this.damages.forEach(damage => {
        const x = damage.coordenadas_x * canvas.width;
        const y = damage.coordenadas_y * canvas.height;
        
        // Círculo exterior
        ctx.strokeStyle = '#000';
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.arc(x, y, 15, 0, 2 * Math.PI);
        ctx.stroke();
        
        // Círculo de color
        ctx.fillStyle = damage.color;
        ctx.beginPath();
        ctx.arc(x, y, 12, 0, 2 * Math.PI);
        ctx.fill();
        
        // Número del daño
        ctx.fillStyle = '#fff';
        ctx.font = 'bold 14px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(damage.numero.toString(), x, y);
      });
    },
    
    startDrawing(e) {
      this.isDrawing = true;
      const rect = this.$refs.canvas.getBoundingClientRect();
      this.lastX = e.clientX - rect.left;
      this.lastY = e.clientY - rect.top;
      
      if (this.currentTool === 'mark') {
        this.addDamageMarker(this.lastX, this.lastY);
      }
    },
    
    draw(e) {
      if (!this.isDrawing || this.currentTool === 'mark') return;
      
      const canvas = this.$refs.canvas;
      const ctx = canvas.getContext('2d');
      const rect = canvas.getBoundingClientRect();
      
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      
      ctx.strokeStyle = this.currentTool === 'erase' ? '#f5f5f5' : this.currentColor;
      ctx.lineWidth = this.brushSize;
      ctx.lineCap = 'round';
      
      ctx.beginPath();
      ctx.moveTo(this.lastX, this.lastY);
      ctx.lineTo(x, y);
      ctx.stroke();
      
      this.lastX = x;
      this.lastY = y;
    },
    
    stopDrawing() {
      if (this.isDrawing) {
        this.isDrawing = false;
        this.saveCurrentCanvas();
      }
    },
    
    handleTouch(e) {
      e.preventDefault();
      const touch = e.touches[0];
      const mouseEvent = new MouseEvent(e.type.replace('touch', 'mouse'), {
        clientX: touch.clientX,
        clientY: touch.clientY
      });
      this.$refs.canvas.dispatchEvent(mouseEvent);
    },
    
    addDamageMarker(x, y) {
      const canvas = this.$refs.canvas;
      const ctx = canvas.getContext('2d');
      
      const damage = {
        tipo: this.damageType,
        ubicacion: this.getLocationDescription(x, y, canvas),
        coordenadas_x: x / canvas.width,
        coordenadas_y: y / canvas.height,
        color: this.currentColor,
        numero: this.damages.length + 1,
        descripcion: ''
      };
      
      this.damages.push(damage);
      
      // Dibujar marcador con borde negro
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(x, y, 15, 0, 2 * Math.PI);
      ctx.stroke();
      
      ctx.fillStyle = this.currentColor;
      ctx.beginPath();
      ctx.arc(x, y, 12, 0, 2 * Math.PI);
      ctx.fill();
      
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 14px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(damage.numero.toString(), x, y);
      
      this.emitUpdate();
    },
    
    getLocationDescription(x, y, canvas) {
      const relX = x / canvas.width;
      const relY = y / canvas.height;
      
      let horizontal = '';
      let vertical = '';
      
      if (relX < 0.33) horizontal = 'Izquierda';
      else if (relX > 0.66) horizontal = 'Derecha';
      else horizontal = 'Centro';
      
      if (relY < 0.33) vertical = 'Superior';
      else if (relY > 0.66) vertical = 'Inferior';
      else vertical = 'Medio';
      
      return `${vertical} ${horizontal}`;
    },
    
    removeDamage(index) {
      this.damages.splice(index, 1);
      // Renumerar los daños
      this.damages.forEach((damage, idx) => {
        damage.numero = idx + 1;
      });
      this.drawVehicleTemplate();
      this.emitUpdate();
    },
    
    clearCanvas() {
      if (confirm('¿Desea limpiar todos los marcadores del diagrama?')) {
        this.damages = [];
        this.drawVehicleTemplate();
        this.emitUpdate();
      }
    },
    
    emitUpdate() {
      const canvas = this.$refs.canvas;
      const canvasImage = canvas ? canvas.toDataURL('image/png') : null;
      
      this.$emit('input', {
        marcas_daños: this.damages,
        canvas_image: canvasImage
      });
      
      this.$emit('update', {
        marcas_daños: this.damages,
        canvas_image: canvasImage
      });
    },
    
    getCanvasImage() {
      const canvas = this.$refs.canvas;
      return canvas ? canvas.toDataURL('image/png') : null;
    }
  }
};
</script>

<style scoped>
.vehicle-diagram {
  width: 100%;
}

.canvas-container {
  width: 100%;
}

.canvas-wrapper {
  width: 100%;
  position: relative;
  background: #f5f5f5;
  border-radius: 4px;
  overflow: hidden;
}

canvas {
  display: block;
  width: 100%;
  cursor: crosshair;
  touch-action: none;
}

.selected-damage {
  background-color: #e3f2fd;
}
</style>
